import React, {
  useState, useContext, useMemo, useRef, useEffect,
} from 'react';
import { GroundType } from 'models/ground';
import { CursorContext } from '../track/TrackChartContainer';
import { useQueryCached } from '../utils/graphql';
import { GroundRes, GroundVars, GROUND_DETAILS } from '../../query/grounds';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import Error from '../layout/Error';
import {
  coordsGPSToLPS, getPlayersLines, getPlayersPointsGPS, getSizesFromRef,
} from '../utils/groundGbf';
import GroundField from './GroundField';
import { useDebouncedResize } from '../../lib/dom';
import { GPSGroundProps } from '../../types/webPlayer';
import { computeRectangleCenter } from '../track/ground/calc';
import { FULLSCREEN_SCALE_MULTIPLIER } from './utils';

const GroundGPS = ((props: GPSGroundProps) => {
  const {
    activePlayer,
    hoverPlayer,
    defaultPlayersColor,
    enabledPlayers,
    fullscreenMode,
    groundId,
    infoPlayers,
    jerseyOrNumber,
    onPlayerClick,
    onPlayerEnter,
    onPlayerLeave,
    playerLinks,
    playersColors,
    playersDetails,
    removeLink,
    series,
    showGroundName,
    showVertices,
    teamColors,
    trailsEnabled,
    userUnits,
  } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState(1);
  const [bearing, setBearing] = useState(0);
  const [scale, setScale] = useState(1);
  const [fullscreenScaleMultiplier, setFullscreenScaleMultiplier] = useState(fullscreenMode ? FULLSCREEN_SCALE_MULTIPLIER : 1);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [angle, setAngle] = useState(0);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [groundData, setGroundData] = useState<GroundType | null>(null);
  const [lpsGroundData, setLpsGroundData] = useState<GroundType | undefined>(undefined);
  const { cursor } = useContext(CursorContext);
  const [vertex, setVertex] = useState({
    vertexA: {
      x: 0,
      y: 0,
    },
    vertexB: {
      x: 0,
      y: 0,
    },
    vertexC: {
      x: 0,
      y: 0,
    },
    vertexD: {
      x: 0,
      y: 0,
    },
  });

  const [windowSize, setWindowSize] = useState({
    WINDOW_WIDTH: 0,
    WINDOW_HEIGHT: 0,
  });

  useEffect(() => {
    setWindowSize(getSizesFromRef(parentRef));
  }, []);

  useDebouncedResize(() => {
    setWindowSize(getSizesFromRef(parentRef));
  }, 200);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setWindowSize(getSizesFromRef(parentRef));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const {
    WINDOW_WIDTH, WINDOW_HEIGHT,
  } = windowSize;

  const {
    error: errorGround,
  } = useQueryCached<GroundRes, GroundVars>(GROUND_DETAILS, {
    variables: {
      id: groundId,
    },
    skip: groundId == null,
    ...CACHE_AND_NETWORK,
    onCompleted: (response) => {
      const {
        vertexA,
        vertexB,
        vertexC,
        vertexD,
        scale: scaleValue,
        localBearing,
      } = coordsGPSToLPS({
        ground: response.res,
        WINDOW_WIDTH,
        WINDOW_HEIGHT,
      });

      setGroundData(response.res);

      // sovrascrivo i dati locali nei dettagli del ground da passare al Pitch
      setLpsGroundData({
        ...response.res,
        ...{
          vertexAX: { ...response.res.vertexAX, ...{ value: vertexA.x } },
          vertexAY: { ...response.res.vertexAY, ...{ value: vertexA.y } },
          vertexBX: { ...response.res.vertexBX, ...{ value: vertexB.x } },
          vertexBY: { ...response.res.vertexBY, ...{ value: vertexB.y } },
          vertexCX: { ...response.res.vertexCX, ...{ value: vertexC.x } },
          vertexCY: { ...response.res.vertexCY, ...{ value: vertexC.y } },
          vertexDX: { ...response.res.vertexDX, ...{ value: vertexD.x } },
          vertexDY: { ...response.res.vertexDY, ...{ value: vertexD.y } },
        },
      });

      // calcolo le dimensioni del campo (dai dati)
      const width = Math.abs(vertexC.x - vertexA.x);
      const height = Math.abs(vertexC.y - vertexA.y);

      const centro = computeRectangleCenter(vertexA, vertexC);

      setBearing(localBearing);

      setVertex({
        vertexA: {x: vertexA.x * scaleValue, y: -vertexA.y * scaleValue},
        vertexB: {x: vertexB.x * scaleValue, y: -vertexB.y * scaleValue},
        vertexC: {x: vertexC.x * scaleValue, y: -vertexC.y * scaleValue},
        vertexD: {x: vertexD.x * scaleValue, y: -vertexD.y * scaleValue},
      });

      setDims({ width: width * scaleValue, height: height * scaleValue });
      setCenter({
        x: centro.x * scaleValue,
        y: -centro.y * scaleValue,
      });
      setScale(scaleValue * (fullscreenMode ? 0.8 : 1) );
    },
  });

  useEffect(() => {
    setFullscreenScaleMultiplier(fullscreenMode ? FULLSCREEN_SCALE_MULTIPLIER : 1);
  }, [fullscreenMode]);

  const effectiveScale = scale * fullscreenScaleMultiplier;

  const scaledWidth = vertex.vertexC.x !== undefined && vertex.vertexC.x !== null
  && vertex.vertexA.x !== undefined && vertex.vertexA.x !== null
    ? Math.abs(vertex.vertexC.x - vertex.vertexA.x)
    : 1;
  const scaledHeight = vertex.vertexA.y !== undefined && vertex.vertexA.y !== null
  && vertex.vertexC.y !== undefined && vertex.vertexC.y !== null
    ? Math.abs(vertex.vertexC.y - vertex.vertexA.y)
    : 1;

  const offX = vertex.vertexA.x - (WINDOW_WIDTH - scaledWidth) / 2;
  const offY = vertex.vertexC.y - (WINDOW_HEIGHT - scaledHeight) / 2;

  /*
  @todo varie
  selettore nome breve o jersey
  linee tra i player con la distanza tra i punti (A-B, C-D, A-C)
  area tra i vari punti
  selettore velocità
  integrare con sessions (se singolo drill mostrare solo quello)
  full screen controlli traccia su hover 1/5 inferiore

  sentire loris per la parte GPS
   */

  const playersPoints = useMemo(() => getPlayersPointsGPS({
    bearing,
    cursor,
    enabledPlayers,
    groundData,
    scale: effectiveScale,
    series,
  }), [scale, bearing, groundData, series, cursor, enabledPlayers, fullscreenScaleMultiplier, effectiveScale]);

  const playersLines = useMemo(() => getPlayersLines({
    playerLinks,
    playersPoints,
  }), [playerLinks, playersPoints]);

  if (errorGround) return <Error />;

  return (
    <>
      {showGroundName
        && (
          <div>
            {lpsGroundData?.id}
            {' '}
            -
            {lpsGroundData?.name}
          </div>
        )}

      <GroundField
        WINDOW_HEIGHT={WINDOW_HEIGHT}
        WINDOW_WIDTH={WINDOW_WIDTH}
        activePlayer={activePlayer}
        bearing={bearing}
        cursor={cursor}
        dataGround={lpsGroundData}
        defaultPlayersColor={defaultPlayersColor}
        enabledPlayers={enabledPlayers}
        groundGeometry={{
          angle,
          scale: effectiveScale,
          vertex,
          dims,
          center,
          scaledDims: {
            width: scaledWidth,
            height: scaledHeight,
          },
        }}
        hoverPlayer={hoverPlayer}
        infoPlayers={infoPlayers}
        jerseyOrNumber={jerseyOrNumber}
        offX={offX}
        offY={offY}
        onPlayerClick={onPlayerClick}
        onPlayerEnter={onPlayerEnter}
        onPlayerLeave={onPlayerLeave}
        playersColors={playersColors}
        playersDetails={playersDetails}
        playersLines={playersLines}
        playersPoints={playersPoints}
        ref={parentRef}
        removeLink={removeLink}
        scale={effectiveScale}
        series={series}
        setZoom={setZoom}
        showVertices={!!showVertices}
        teamColors={teamColors}
        trailsEnabled={trailsEnabled}
        userUnits={userUnits}
        zoom={zoom}
      />
    </>
  );
});

export default GroundGPS;
